<template>
  <div id="MainPage" class="tourLp">
    <!-- 検索条件エリアは画面上部に固定表示するため一番上に配置している -->
    <tk-sticky-item
      v-if="mobile"
      :z-index="1000"
    >
      <search-condition-box
        :class="['tourLp_searchConditionBox', { '-visible': isHeaderPinned }]"
        :condition="condition"
        :url="condition.hasMultipleDestinations ? urlForSearchAgain : undefined"
        :disabled="isLoadingMaster"
        button-text="+ 条件変更"
        icon-color="red"
        button-theme="round-corners"
        @execute="isSearchModalOpened = true"
      />
    </tk-sticky-item>
    <div
      v-if="!mobile"
      class="tourLp_breadcrumbsArea"
    >
      <breadcrumbs :condition="condition" />
    </div>
    <div class="tourLp_titleArea">
      <div class="tourLp_imageArea">
        <tk-image
          v-if="imageUrl"
          class="tourLp_titleImage"
          :image-path="imageUrl"
        />
      </div>
      <div class="tourLp_textArea">
        <!-- eslint-disable vue/no-v-html -->
        <div
          v-if="pageDefinition.title"
          class="tourLp_title"
          v-html="pageDefinition.title"
        />
        <div
          v-if="pageDefinition.description"
          class="tourLp_description"
          v-html="pageDefinition.description"
        />
        <!-- eslint-enable vue/no-v-html -->
      </div>
    </div>
    <!-- z-index: 12 はツールチップのデフォルト値 10 より大きい数値 -->
    <component
      :is="mobile ? 'div' : 'tk-sticky-item'"
      class="tourLp_searchFormArea"
      :z-index="12"
    >
      <tk-skeleton-loader
        v-if="isLoadingMaster"
        class="tourLp_searchFormLoading"
      />
      <search-form
        v-else
        v-model="editedSearchCondition"
        class="tourLp_searchForm"
        :layout="mobile ? 'compact' : 'single-line'"
        :max-number-of-tours-to-display="maxNumberOfToursToDisplay"
        theme="flat"
        size="xxx-large"
        simple-placeholder
        departure-icon
        destination-icon
        active-destination-tab="area"
        date-icon
        passenger-icon
        :put-icon-outside="!mobile"
        icon-color="red"
        :icon-separator="mobile ? 'default' : undefined"
        @execute="searchAgain"
      />
    </component>
    <div class="tourLp_pickedUpIntentionArea">
      <tk-skeleton-loader
        v-if="isLoadingMaster"
        class="tourLp_pickedUpIntentionLoading"
      />
      <div
        v-else
        class="tourLp_pickedUpIntention"
      >
        <checkbox
          v-if="pageDefinition.intentionId"
          :selected="false"
          :value="pageDefinition.intentionId.split('-')"
          appearance="button"
          size="large"
          :label="allFacets.findIntentionNameById(pageDefinition.intentionId.split('-')[0])"
          disabled
        />
        <template v-if="pickedUpIntentionIds.length > 0">
          <checkbox
            v-for="id in pickedUpIntentionIds"
            :key="id"
            :selected="condition.intentionIds"
            :value="id.split('-')"
            appearance="button"
            size="large"
            :label="allFacets.findIntentionNameById(id.split('-')[0])"
            :disabled="!shouldEnableIntentionCheckbox(id.split('-'))"
            @change="updateIntentions($event, id.split('-'))"
          />
        </template>
      </div>
    </div>
    <tk-sticky-item
      v-model="isHeaderPinned"
      :top="listHeaderTop"
      :z-index="11"
    >
      <template v-slot="{ isPinned }">
        <div :class="['tourLp_listHeaderArea', { '-pinned': isPinned }]">
          <div class="tourLp_listHeader">
            <div
              v-if="mobile"
              class="tourLp_sortArea"
            >
              <tk-pulldown
                :items="[
                  { label: 'おすすめ順', value: 'price_fav' },
                  { label: '料金（安い順）', value: 'price_asc' },
                  { label: '料金（高い順）', value: 'price_desc' },
                  { label: 'ホテルランク順', value: 'hotelrank_desc' },
                  { label: '人気順', value: 'pageview' },
                ]"
                :value="order"
                :disabled="isLoadingMaster"
                theme="flat"
                size="xx-large"
                icon="filled-spin"
                icon-color="blue"
                text-color="blue"
                bold
                no-border
                @change="updateOrder($event)"
              />
              <div class="tourLp_sortAreaSeparator" />
            </div>
            <div
              v-else
              class="tourLp_sortArea"
            >
              <tk-button
                type="button"
                :class="['tourLp_sortButton', { '-active': order === 'price_fav' }]"
                :disabled="isLoadingMaster"
                @click="updateOrder('price_fav')"
              >
                おすすめ順
              </tk-button>
              <tk-custom-pulldown
                :value="['price_asc', 'price_desc'].includes(order) ? order : null"
                :items="[{ label: '料金（安い順）', value: 'price_asc' }, { label: '料金（高い順）', value: 'price_desc' }]"
                :disabled="isLoadingMaster"
                unselected-label="料金（安い順）"
                theme="flat"
                size="large"
                :width="148"
                icon="filled-spin"
                icon-color="blue"
                text-color="blue"
                bold
                :active="['price_asc', 'price_desc'].includes(order)"
                @change="updateOrder($event)"
              />
              <tk-button
                type="button"
                :class="['tourLp_sortButton', '-hotelrank', { '-active': order === 'hotelrank_desc' }]"
                :disabled="isLoadingMaster"
                @click="updateOrder('hotelrank_desc')"
              >
                ホテルランク順
              </tk-button>
              <tk-button
                type="button"
                :class="['tourLp_sortButton', { '-active': order === 'pageview' }]"
                :disabled="isLoadingMaster"
                @click="updateOrder('pageview')"
              >
                人気順
              </tk-button>
            </div>
            <div class="tourLp_listHeaderRight">
              <filter-button
                :color="mobile ? 'blue' : undefined"
                :disabled="isLoadingTours"
                :checkmark="condition.hasFilters"
                :no-border="mobile"
                @click="isFilterModalOpened = true"
              />
              <div
                v-if="!priorSaleType && !isLoadingTours"
                class="tourLp_countArea"
              >
                <span>全</span>
                <span class="tourLp_count">{{ total > maxNumberOfToursToDisplay ? maxNumberOfToursToDisplay : total }}</span>
                <span>件{{ total > maxNumberOfToursToDisplay ? '以上' : '' }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </tk-sticky-item>
    <div class="tourLp_contentsArea">
      <div
        v-if="!!priorSaleType"
        class="tourLp_listArea"
      >
        <!-- ref は jest 用 -->
        <tk-declarative-tabs
          ref="tourTabs"
          nav-tabs-class="tourLp_tourListTabs"
          active-class="active"
          content-class="tourLp_tab-content"
          :active-tab-index="activeTourTabIndex"
          @select-tab="selectTourTab"
        >
          <template v-slot:title="{ title }">
            <!-- eslint-disable vue/no-v-html -->
            <div v-html="title"></div>
          </template>

          <!-- オンラインタブ -->
          <tk-tab :title="createTabTitle(onlineTabTitle, totalOnlineTours)">
            <div class="tourLp_cassetteArea">
              <tk-list-loading v-if="isLoadingTours" />
              <tour-list
                v-else-if="onlineTours.length > 0"
                :tours="onlineTours"
                :make-detail-page-url="makeDetailPageUrl"
                :mobile="mobile"
              />
              <!-- [暫定]ツアー0件の場合history back対応 開始
              <order-made-link-cassette v-else />
              -->
              <tour-not-exist-cassette v-else />
              <!-- [暫定]ツアー0件の場合history back対応 終了 -->
            </div>
            <div
              v-if="!isLoadingTours && hasMoreOnlineTour"
              class="tourLp_displayMoreArea"
            >
              <!-- ref は jest 用 -->
              <tk-busy-cursor-button
                ref="moreOnlineTours"
                displayed-label="もっと見る"
                :loading="isLoadingMore"
                @pushed="fetchMoreTours(onlineTours)"
              />
            </div>
          </tk-tab>

          <!-- オフラインタブ -->
          <tk-tab :title="createTabTitle(offlineTabTitle, totalOfflineTours)">
            <div class="tourLp_cassetteArea">
              <tk-list-loading v-if="isLoadingTours" />
              <tour-list
                v-else-if="offlineTours.length > 0"
                :tours="offlineTours"
                :make-detail-page-url="makeDetailPageUrl"
                :mobile="mobile"
              />
              <!-- [暫定]ツアー0件の場合history back対応 開始
              <order-made-link-cassette v-else />
              -->
              <tour-not-exist-cassette v-else />
              <!-- [暫定]ツアー0件の場合history back対応 終了 -->
            </div>
            <div
              v-if="!isLoadingTours && hasMoreOfflineTour"
              class="tourLp_displayMoreArea"
            >
              <!-- ref は jest 用 -->
              <tk-busy-cursor-button
                ref="moreOfflineTours"
                displayed-label="もっと見る"
                :loading="isLoadingMore"
                @pushed="fetchMoreTours(offlineTours)"
              />
            </div>
          </tk-tab>
        </tk-declarative-tabs>
      </div>
      <div
        v-else
        class="tourLp_listArea">
        <div class="tourLp_cassetteArea -mixed">
          <tk-list-loading v-if="isLoadingTours" />
          <tour-list
            v-else-if="mixedTours.length > 0"
            :tours="mixedTours"
            :make-detail-page-url="makeDetailPageUrl"
            :mobile="mobile"
            duration-tabs="always"
          />
          <tour-not-exist-cassette v-else />
        </div>
        <div
          v-if="!isLoadingTours && hasMoreMixedTour"
          class="tourLp_displayMoreArea -mixed"
        >
          <tk-busy-cursor-button
            displayed-label="もっと見る"
            :loading="isLoadingMore"
            @pushed="fetchMoreTours(mixedTours)"
          />
        </div>
      </div>
    </div>
    <div
      v-if="mobile"
      class="tourLp_breadcrumbsArea"
    >
      <breadcrumbs :condition="condition" />
    </div>
    <tk-general-purpose-modal
      v-if="isSearchModalOpened"
      no-close-on-backdrop
      no-close-on-esc
      no-sp-fullscreen
      close-text="閉じる"
      @close="isSearchModalOpened = false"
    >
      <search-form
        v-model="editedSearchCondition"
        class="tourLp_modalSearchForm"
        passenger-number-unit="/1室"
        :max-number-of-tours-to-display="maxNumberOfToursToDisplay"
        theme="flat"
        size="xxx-large"
        simple-placeholder
        departure-icon
        destination-icon
        active-destination-tab="area"
        date-icon
        passenger-icon
        icon-color="red"
        icon-separator="default"
        @execute="searchAgain"
      />
    </tk-general-purpose-modal>
    <tk-modal
      v-if="isFilterModalOpened"
      class="tourLp_filterModal"
      @close="isFilterModalOpened = false"
    >
      <template slot="topbarTitle">
        絞り込み
      </template>
      <tour-filter
        v-model="filterCondition"
        :size="mobile ? 'x-large' : 'normal'"
        :campaigns="masterData.campaign"
        :max-number-of-tours-to-display="maxNumberOfToursToDisplay"
        content-padding="14px"
        :sticky-button="mobile"
        @filter="applyFilter"
      />
    </tk-modal>
  </div>
</template>

<script>
import {
  TkBusyCursorButton,
  TkButton,
  TkCustomPulldown,
  TkDeclarativeTabs,
  TkGeneralPurposeModal,
  TkImage,
  TkListLoading,
  TkModal,
  TkPulldown,
  TkSkeletonLoader,
  TkStickyItem,
  TkTab,
} from '@ota-frontend/ui'
import { hasIntersection } from '@ota-frontend/core/utils'
import {
  buildTourQueryFromSearchCondition,
  fetchBff,
  fetchTourLpInitialDisplay,
  fetchTourLpFacets,
  fetchTourLpMoreTours,
} from '@/api'
import Breadcrumbs from '@/components/Breadcrumbs'
import Checkbox from '@/components/Checkbox.vue'
import FilterButton from '@/components/FilterButton'
import SearchConditionBox from '@/components/SearchConditionBox'
import SearchForm from '@/components/SearchForm'
import { gtm, meta } from '@/mixins'
import { Areas, DeparturePlaces, Facets, IntentionLp, SearchCondition } from '@/models'
import TourFilter from '@/modules/tour-list/components/TourFilter.vue'
import TourList from '@/modules/lp/components/TourList.vue'
import TourNotExistCassette from '@/modules/tour-list/components/TourNotExistCassette.vue'

// ツアーリストタブ一覧(順番はタブ順と一致させておくこと)
const TOURS_FOR_TABS = [
  'online',
  'offline',
]

export default {
  name: 'TourLp',

  metaInfo() {
    const noIndex = !this.searchCondition.isEmpty
    return this.__tourLpMeta(
      this.pageDefinition.metaTitle,
      this.pageDefinition.metaDescription,
      noIndex,
    )
  },

  components: {
    Breadcrumbs,
    Checkbox,
    FilterButton,
    SearchForm,
    SearchConditionBox,
    TkBusyCursorButton,
    TkButton,
    TkCustomPulldown,
    TkDeclarativeTabs,
    TkGeneralPurposeModal,
    TkImage,
    TkListLoading,
    TkModal,
    TkPulldown,
    TkSkeletonLoader,
    TkStickyItem,
    TkTab,
    TourFilter,
    TourList,
    TourNotExistCassette,
  },

  mixins: [gtm, meta],

  props: {
    // intention名
    name: {
      type: String,
      required: true,
    },
    mobile: {
      type: Boolean,
      required: true,
    },
    searchCondition: {
      type: SearchCondition,
      required: true,
    },
    // props に渡される検索条件を更新するための関数
    updateSearchCondition: {
      type: Function, // SearchCondition => {}, props の更新を促すための関数
      required: true,
    },
    // ツアー詳細画面へのリンクを生成する関数
    makeDetailPageUrl: {
      type: Function, // saleType => tourCode => url
      required: true,
    },
    /**
     * 検索表示出来るツアーの最大件数
     * Elasticsearchの仕様により現状検索APIが返せる最大件数は10000件
     */
    maxNumberOfToursToDisplay: {
      type: Number,
      default: 10000,
    },
    // props として指定された name に対応する定義が見つからなかった場合に呼ばれるエラーハンドラ
    onMissingDefinition: {
      type: Function, // () => {}
      required: true,
    },
  },

  data() {
    return {
      isLoadingMaster: false,  // 現状、初期表示BFF待ち合わせのタイミングのみ true となる
      isLoadingTours: false,
      isLoadingFacets: false,
      isSearchModalOpened: false,
      isLoadingMore: false,
      isFilterModalOpened: false,
      pageDefinition: new IntentionLp(),
      editedSearchCondition: new SearchCondition(),
      filterCondition: this.searchCondition.empty,
      mixedTours: [],
      onlineTours: [],
      offlineTours: [],
      masterData: {},
      areas: new Areas(),
      departurePlaces: new DeparturePlaces(),
      facets: new Facets(),
      allFacets: new Facets(),
      priorSaleType: undefined,
      totalMixedTours: 0,
      totalOnlineTours: 0,
      totalOfflineTours: 0,
      activeTourTab: 'online',

      // スクロール時のヘッダー部分のスタイル切り替え関係
      listHeaderTop: this.mobile ? 67 : 83,
      isHeaderPinned: false,
    }
  },

  computed: {
    condition() {
      return this.pageDefinition.applyDefaults(this.searchCondition)
        .withAreas(this.areas)
        .withDeparturePlaces(this.departurePlaces)
        .withFacets(this.allFacets)
    },
    imageUrl() {
      return this.pageDefinition.image[this.mobile ? 'sp' : 'pc']
    },
    pickedUpIntentionIds() {
      return this.pageDefinition.pickedUpIntentionIds
        .filter(id => this.allFacets.isIntentionSelectable(id.split('-')))
    },
    total() {
      return this.priorSaleType
        ? this.totalOnlineTours + this.totalOfflineTours
        : this.totalMixedTours
    },
    activeTourTabIndex() {
      return TOURS_FOR_TABS.findIndex(t => t === this.activeTourTab)
    },
    hasMoreOnlineTour() {
      return this.totalOnlineTours > this.onlineTours.length
    },
    hasMoreOfflineTour() {
      return this.totalOfflineTours > this.offlineTours.length
    },
    hasMoreMixedTour() {
      return this.totalMixedTours > this.mixedTours.length
    },
    onlineTabTitle() {
      return this.condition.isDomestic ? '厳選セットプラン' : '宿+航空券'
    },
    offlineTabTitle() {
      return this.condition.isDomestic ? '特典・こだわりプラン' : 'ツアー'
    },
    order() {
      return this.condition.order || SearchCondition.ORDERS[0].code
    },

    // watch によるツアー一覧更新検知用
    allTours() {
      return [...this.mixedTours, ...this.onlineTours, ...this.offlineTours]
    },
  },

  watch: {
    searchCondition(newCondition, oldCondition) {
      this.fetchTours()
      this.fetchFacets()
      this.isFilterModalOpened = false
      this.isSearchModalOpened = false
      this.editedSearchCondition = this.condition.clone()
      this.filterCondition = this.condition.clone()
    },
    allTours() {
      if (this.$gtm && this.$gtm.enabled()) {
        this.$gtm.trackEvent({
          event: 'custom.static-tour.lp',
          ...this.__gtmDataLayerOnNewTourList(this.condition),
        })
      }
    },
  },

  async beforeMount() {
    this.editedSearchCondition = this.condition.clone()
    await this.fetchInitialDisplayInfo()
    await this.fetchFacets()
  },

  methods: {
    async fetchInitialDisplayInfo() {
      this.isLoadingMaster = true
      this.isLoadingTours = true

      const condition = this.condition
      const initialDisplayInfo = await fetchTourLpInitialDisplay(condition, this.name)
      // await 後、ヒストリーバック等で検索条件が変わっていることがあり、その場合はこの関数自体をキャンセルする
      if (this.condition !== condition) return

      if (initialDisplayInfo.code === 'NOT_FOUND') {
        // props として渡された name が古かったり不正だったりして定義が見つからなかった場合
        this.onMissingDefinition()
        return
      }

      this.mixedTours = initialDisplayInfo.mixed ? initialDisplayInfo.mixed.tours : []
      this.onlineTours = initialDisplayInfo.online ? initialDisplayInfo.online.tours : []
      this.offlineTours = initialDisplayInfo.offline ? initialDisplayInfo.offline.tours :[]
      this.masterData = initialDisplayInfo.master_data
      this.pageDefinition = new IntentionLp(initialDisplayInfo.master_data.intention_lp)
      this.areas = new Areas(initialDisplayInfo.master_data.area)
      this.departurePlaces = new DeparturePlaces(initialDisplayInfo.master_data.departure ? [initialDisplayInfo.master_data.departure] : [])
      this.totalMixedTours = initialDisplayInfo.mixed ? initialDisplayInfo.mixed.total : 0
      this.totalOnlineTours = initialDisplayInfo.online ? initialDisplayInfo.online.total : 0
      this.totalOfflineTours = initialDisplayInfo.offline ? initialDisplayInfo.offline.total: 0
      this.allFacets = new Facets(initialDisplayInfo.all_facets)
      this.priorSaleType = initialDisplayInfo.default_tab
      this.activeTourTab = initialDisplayInfo.default_tab

      this.editedSearchCondition = this.condition.clone()
      this.filterCondition = this.condition.clone()

      if (this.activeTourTab === 'online' && this.onlineTours.length === 0 && this.offlineTours.length > 0) {
        this.activeTourTab = 'offline'
      } else if (this.activeTourTab === 'offline' && this.offlineTours.length === 0 && this.onlineTours.length > 0) {
        this.activeTourTab = 'online'
      }

      this.isLoadingMaster = false
      this.isLoadingTours = false
    },
    async fetchTours() {
      this.isLoadingTours = true

      const condition = this.condition
      const tours = await fetchBff('tour/lp/tours', {
        ...buildTourQueryFromSearchCondition(condition),
        sale_type: this.priorSaleType ? 'both' : 'mixed',
      })
      // await 後、ヒストリーバック等で検索条件が変わっていることがあり、その場合はこの関数自体をキャンセルする
      if (this.condition !== condition) return

      this.mixedTours = tours.mixed ? tours.mixed.tours : []
      this.onlineTours = tours.online ? tours.online.tours : []
      this.offlineTours = tours.offline ? tours.offline.tours : []
      this.totalMixedTours = tours.mixed ? tours.mixed.total : 0
      this.totalOnlineTours = tours.online ? tours.online.total : 0
      this.totalOfflineTours = tours.offline ? tours.offline.total : 0

      this.isLoadingTours = false
    },
    async fetchMoreTours(targetTours) {
      this.isLoadingMore = true
      const condition = this.condition
      const { tours } = await fetchTourLpMoreTours(
        condition,
        String(targetTours.length),
        this.priorSaleType ? this.activeTourTab : null,
      )
      // await 後、ヒストリーバック等で検索条件が変わっていることがあり、その場合はこの関数自体をキャンセルする
      if (this.condition !== condition) return
      targetTours.push(...tours)
      this.isLoadingMore = false
    },
    async fetchFacets() {
      this.isLoadingFacets = true
      const { facets } = await fetchTourLpFacets(this.condition)
        .catch(e => ({ facets: this.facets.raw }))  // 失敗したときは現在と同じ値にフォールバックしておく
      this.facets = new Facets(facets, this.condition.isDomestic)
      this.isLoadingFacets = false
    },
    searchAgain() {
      this.updateSearchCondition(this.editedSearchCondition, {
        normalizeLocation: true,
        passiveParameters: {
          research: 1,  // 計測用パッシブパラメータ
        }
      })
      this.isSearchModalOpened = false
    },
    applyFilter() {
      this.updateSearchCondition(
        this.searchCondition.withoutFilters.update(this.filterCondition.filters.raw)
      )
      this.isFilterModalOpened = false
    },
    updateOrder(order) {
      if (!this.searchCondition.order && order === 'price_fav') return

      this.updateSearchCondition(this.searchCondition.update({
        [SearchCondition.PARAMS.ORDER]: order,
      }))
    },
    selectTourTab({ tabIndex }) {
      this.activeTourTab = TOURS_FOR_TABS[tabIndex]
    },
    formatTotalTours(total) {
      if (this.isLoadingTours) {
        return '（- 件）'
      }
      return this.maxNumberOfToursToDisplay < total
        ? `（${this.maxNumberOfToursToDisplay.toLocaleString()}件以上）`
        : `（${total.toLocaleString()} 件）`
    },
    createTabTitle(title, total) {
      return `<span class='tourLp_tourListTabTitle'>${title}</span>
              <br>${this.formatTotalTours(total)}`
    },
    // チェックの ON OFF を確認するためだけに渡される selected が可読性を下げている
    updateIntentions(selected, codes) {
      const checked = hasIntersection(selected, codes)
      const updated = checked
        ? this.condition.selectIntention(codes)
        : this.condition.unselectIntention(codes)
      this.updateSearchCondition(updated.filters.withDefaults(this.searchCondition))
    },
    shouldEnableIntentionCheckbox(codes) {
      // facets取得中またはツアー情報ロード中はdisabled
      if (this.isLoadingFacets || this.isLoadingTours) return false

      // 人気の絞り込み条件はソースコード上に直書きされており、選択可能かどうかの情報としては使えないため明示的に除外する
      // (それらの条件が選択可能なら fetchFacets の結果に含まれているはずで、withoutPopularIntentions をしても残る)
      const facets = this.facets.withoutPopularIntentions

      // 対象ツアーありで選択可能、または既に選択中（再選択不可でも選択解除だけはできるようにしておく）
      return facets.isIntentionSelectable(codes) || this.condition.hasIntention(codes)
    },
  },
}
</script>

<style scoped lang="scss">
.tourLp {
  position: relative;
}
.tourLp_breadcrumbsArea {
  margin: 0 auto;
  max-width: 980px;
  padding: 10px 10px 0 10px;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;
  font-size: 11px;
  background: #f4f4f4;
}
.tourLp_titleArea {
  position: relative;
  width: 100%;
  height: 540px;

  @media (max-width: 657px) {
    height: 411px;
  }
}
.tourLp_imageArea {
  z-index: -1;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  overflow: hidden;
  background: linear-gradient(#79bcf7 25%, #bac288);
}
.tourLp_titleImage {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.tourLp_textArea {
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: absolute;
  top: 0;
  bottom: 0;
  gap: 27px;
  width: 100%;
  color: #fff;

  @media (max-width: 657px) {
    gap: 17px;
    height: calc(100% - 68px); // 再検索フォームが被っている部分を除いて指定
  }
}
.tourLp_title {
  font-size: 70px;
  font-weight: bold;
  text-align: center;
  letter-spacing: 7px;
  text-shadow: 0px 0px 6px #000000A6;

  @media (max-width: 657px) {
    font-size: unquote("min(7.729468599vw, 34px)");  // 幅 414 のデバイスで 32px になる値で、最大 34px まで幅に応じて増減
    letter-spacing: 3.2px;
    text-shadow: 0px 0px 4px #000000A6;
  }
}
.tourLp_description {
  width: 760px;
  margin: 0 auto;
  padding: 27px;
  background-color: rgba(0, 0, 0, 0.3);
  font: normal normal medium 15px/24px Yu Gothic;
  font-size: 20px;
  font-weight: 500;
  line-height: 1.7;
  letter-spacing: 1px;
  text-align: center;

  @media (max-width: 657px) {
    width: auto;
    margin: 0 15px;
    padding: 21px 2px;
    font-size: unquote("min(3.381642512vw, 16px)");  // 幅 414 のデバイスで 14px になる値で、最大 16px まで幅に応じて増減
    letter-spacing: 0.38px;
  }
}
.tourLp_searchFormArea {
  padding: 16.5px 0;
  box-shadow: 0px 1px 1px rgba(0, 0, 0, .1);
  background-color: #fff;

  @media (max-width: 657px) {
    position: relative;
    top: -68px;
    margin: 0 15px;
    padding: 0;
    box-shadow: none;
    background: transparent;
  }
}
.tourLp_searchFormLoading {
  display: block;
  width: 960px;
  height: 50px;
  margin: auto;

  @media (max-width: 657px) {
    position: absolute;
    width: 100%;
    height: 193px;
    border-radius: 8px;
    box-shadow: 0px 2px 2px #00000029;
  }
}
.tourLp_searchForm {
  max-width: 960px;
  margin: auto;

  @media (max-width: 657px) {
    position: absolute;
    width: 100%;
    border-radius: 8px;
    box-shadow: 0px 2px 2px #00000029;
  }
}
.tourLp_modalSearchForm {
  border-radius: 0 0 8px 8px;
}
.tourLp_pickedUpIntentionArea {
  display: flex;
  height: 70px;
  background: #EFEFEF;

  @media (max-width: 657px) {
    height: 195px;
    padding-top: 125px;
  }
}
.tourLp_pickedUpIntentionLoading {
  width: 100%;
  height: 70px;
}
.tourLp_pickedUpIntention {
  display: flex;
  gap: 8px;
  margin: 0 auto;
  width: 100%;
  max-width: 960px;
  font-size: 15px;
  letter-spacing: 1.5px;

  @media (max-width: 657px) {
    padding-left: 8px;
    white-space: nowrap;
    overflow: auto;
  }
}
.tourLp_searchConditionBox {
  position: absolute;
  visibility: hidden;
  top: 0;
  width: 100%;
  height: 67px;
  border-bottom: solid 1px #d2d2d2;

  &.-visible {
    visibility: visible;
  }
}
.tourLp_listHeaderArea {
  margin: 7px auto;
  background-color: white;

  &.-pinned {
    box-shadow: 0px 2px 2px rgba(0, 0, 0, .1);
  }

  @media (max-width: 657px) {
    width: 100%;
    height: 45px;
    margin: auto auto 13px;
    box-shadow: 0px 2px 2px rgba(0, 0, 0, .1);

    &.-pinned {
      box-shadow: 0px 4px 4px rgba(0, 0, 0, .1);
    }
  }
}
.tourLp_listHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 960px;
  height: 62px;
  margin: 0 auto;

  @media (max-width: 657px) {
    width: 100%;
    height: 45px;
    margin-bottom: 13px;
    gap: 4px;
  }
}
.tourLp_sortArea {
  display: flex;
  align-items: center;
  gap: 6px;

  @media (max-width: 657px) {
    gap: 0px;
  }
}
.tourLp_sortButton {
  width: 114px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #bbd2e6;
  border-radius: 4px;
  background: white;
  color: #1f6aad;
  font-size: 15px;
  font-weight: bold;
  line-height: 36px;
  cursor: pointer;

  &.-hotelrank {
    width: 140px;
  }

  &.-active {
    border: none;
    background: #1f6aad;
    color: #ffffff;
  }

  &:focus {
    outline: 0;
  }
}
.tourLp_sortAreaSeparator {
  width: 1px;
  height: 16px;
  background-color: #707070;
  opacity: .3;
}
.tourLp_listHeaderRight {
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  height: 100%;

  @media (max-width: 657px) {
    display: flex;
    justify-content: space-between;
  }
}
.tourLp_countArea {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;

  @media (max-width: 657px) {
    margin-right: 11px;
  }
}
.tourLp_count {
  font-size: 22px;
  font-weight: bold;

  @media (max-width: 657px) {
    font-size: 20px;
  }
}
.tourLp_contentsArea {
  margin: 0 auto;

  @media (max-width: 657px) {
    max-width: 657px;
  }
}
.tourLp_listArea {
  margin-bottom: 15px;
}
.tourLp_cassetteArea {
  padding: 15px 10px 5px;
  max-width: 960px;
  margin: 0 auto;

  @media (max-width: 657px) {
    max-width: 657px;
  }

  &.-mixed {
    padding-bottom: 20px;
    background: none;
  }
}
</style>

<style lang="scss">
.tourLp_tab-content {
  background: #f7f5f0;
  border-top: solid 1px #d2d2d2;
}
.tourLp_tourListTabs {
  display: flex;
  position: relative;
  justify-content: space-between;
  width: 960px;
  margin: auto;
  padding: 0 5px;

  @media (max-width: 657px) {
    max-width: 100%;
  }

  & li {
    justify-content: center;
    border-radius: 4px 4px 0 0;
    background: #e7e7e7;
    border: 1px solid #d2d2d2;
    border-bottom: none;
    padding: 10px 0;
    width: calc(50% - 2px);
    font-size: 13px;
    float: left;
    cursor: pointer;
    text-align: center;

    &.active {
      position: relative;
      background: #f7f5f0;
      color: #333;
      border-bottom: none;
      font-weight: bold;
      top: 1px;
    }
  }
}
.tourLp_tourListTabTitle {
  font-size: 16px;
}
.tourLp_displayMoreArea {
  padding: 15px 0;
  margin: 0 auto;
  text-align: center;
  box-sizing: border-box;
  background: #f7f5f0;

  &.-mixed {
    background: none;
  }
}
// 別コンポーネントに影響を及ぼす可能性が高い
// 要リファクタ
.modal-open .modal {
  overflow-x: hidden;
  overflow-y: auto;
}
.modal {
  position: fixed;
  padding-right: 0 !important;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1050;
  outline: 0;
}
.modal-content {
  position: relative;
  background-color: #fff;
}
.tourLp_filterModal {
  .tourHotelPlaceModal_topbar {
    text-align: left;
    border-radius: 0;
    color: #fff;
    background-color: #63666a;
    width: 100%;
    padding: 8px 14px;
    position: relative;
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    white-space: nowrap;
    line-height: 1.42857143;
    vertical-align: middle;
    height: auto;
  }
  .tourHotelPlaceModal_title {
    font-size: 17px;
    text-decoration: none;
    -webkit-user-select: none;
    user-select: none;
  }
  .btn.btn-link.tourHotelPlaceModal_close {
    padding: 0;
    margin-bottom: 0;
    touch-action: manipulation;
    cursor: pointer;
    background-image: none;
    border: 1px solid rgba(0, 0, 0, 0);
    min-height: auto;
  }
  .modal-dialog {
    width: 460px;

    @media (max-width: 657px) {
      width: unset;
    }
  }
}
</style>
